// src/components/ProjectCallToAction.styles.ts
//
// Styling for ProjectCallToAction.
//
// The previous version hard-coded #111 / #f5f5f5, painted two radial gradient
// glows over the panel, lifted the entire section 2px on hover and faded itself
// in with a CSS keyframe from `opacity: 0`. All four are gone:
//
//   - The panel colour is derived from the active theme, so the contrast
//     treatment stays legible in light and dark instead of only dark.
//   - No gradients. The panel is flat with a hairline border, which is what
//     the rest of the Home page does.
//   - No whole-section hover lift. Hover feedback belongs to the buttons.
//   - Entrance is Framer Motion, which always settles to its visible state.
//     A keyframe starting at `opacity: 0` leaves the section invisible if the
//     animation never runs.
import styled, { css } from 'styled-components';
import type { DefaultTheme } from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

import { minDevice } from '../styles/breakpoints';

const monoStack =
  "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace";

/* ------------------------------------------------- theme-aware contrast */

const srgbChannel = (value: number) => {
  const v = value / 255;
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
};

/**
 * WCAG relative luminance of a 3- or 6-digit hex colour.
 * Returns 1 (white) for anything unparseable, so an unexpected theme value
 * fails toward the dark-on-light direction rather than throwing.
 */
const relativeLuminance = (hex: string): number => {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return 1;

  let digits = match[1];
  if (digits.length === 3) {
    digits = digits
      .split('')
      .map((d) => d + d)
      .join('');
  }

  const [r, g, b] = [0, 2, 4].map((offset) => parseInt(digits.slice(offset, offset + 2), 16));
  return 0.2126 * srgbChannel(r) + 0.7152 * srgbChannel(g) + 0.0722 * srgbChannel(b);
};

/**
 * The CTA reads as the one contrasting block on the page, so it needs to know
 * which way round the current theme is. Deriving it from the page background
 * means the panel inverts correctly for any theme without theme.ts having to
 * expose a `mode` flag.
 */
const isDarkPage = (theme: DefaultTheme) => relativeLuminance(theme.background) < 0.5;

interface Contrast {
  /** Panel fill. Always a dark step against the page background. */
  panel: string;
  panelBorder: string;
  /** Primary text on the panel. */
  ink: string;
  /** Supporting text on the panel. */
  inkMuted: string;
}

const contrast = (theme: DefaultTheme): Contrast =>
  isDarkPage(theme)
    ? {
        // Dark page: the panel is one step up from the background, so the
        // separation comes from the border as much as from the fill.
        panel: '#141414',
        panelBorder: 'rgba(255, 255, 255, 0.1)',
        ink: '#f5f5f5',
        inkMuted: '#a8a8a8',
      }
    : {
        // Light page: a genuinely dark panel is the contrast, no border needed.
        panel: '#0f0f0f',
        panelBorder: 'rgba(255, 255, 255, 0.14)',
        ink: '#f5f5f5',
        inkMuted: '#a0a0a0',
      };

/* -------------------------------------------------------------- section */

export const CtaRoot = styled.section`
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.borderColor};
  background: ${({ theme }) => theme.background};
  padding: 4.5rem 0;

  @media ${minDevice.laptop} {
    padding: 6rem 0;
  }
`;

export const CtaContainer = styled.div`
  width: 90%;
  max-width: 1120px;
  margin: 0 auto;
`;

export const Panel = styled(motion.div)`
  padding: 2.25rem 1.75rem;
  background: ${({ theme }) => contrast(theme).panel};
  border: 1px solid ${({ theme }) => contrast(theme).panelBorder};
  border-radius: ${({ theme }) => theme.borderRadius.lg};

  @media ${minDevice.tablet} {
    padding: 3.25rem;
  }

  @media ${minDevice.laptop} {
    padding: 4rem;
  }
`;

export const Eyebrow = styled(motion.p)`
  margin: 0 0 1rem;
  font-family: ${monoStack};
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => contrast(theme).inkMuted};
`;

export const Heading = styled(motion.h2)`
  margin: 0;
  max-width: 20ch;
  font-size: 1.6rem;
  line-height: 1.25;
  letter-spacing: -0.01em;
  font-weight: 700;
  color: ${({ theme }) => contrast(theme).ink};

  @media ${minDevice.tablet} {
    font-size: 2rem;
  }

  @media ${minDevice.laptop} {
    font-size: 2.25rem;
  }
`;

export const Message = styled(motion.p)`
  margin: 1.25rem 0 0;
  max-width: 58ch;
  font-size: 1rem;
  line-height: 1.7;
  color: ${({ theme }) => contrast(theme).inkMuted};

  @media ${minDevice.laptop} {
    font-size: 1.05rem;
  }
`;

/* -------------------------------------------------------------- actions */

export const Actions = styled(motion.div)`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;

  @media ${minDevice.tablet} {
    flex-direction: row;
    align-items: center;
    gap: 1.25rem;
  }
`;

/**
 * Shared action treatment. Both variants are anchors: they are navigation, so
 * they need an href for middle-click, "open in new tab" and screen readers to
 * announce them as links.
 */
const actionBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* Comfortable touch target at every breakpoint. */
  min-height: 48px;
  padding: 0.8rem 1.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 0.975rem;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  /* Transform-only hover, so a press cannot reflow the row beside it. */
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease,
    transform 0.2s ease;

  /* The ring is drawn in the panel colour so it reads against the button's own
     resting fill, not the page background.

     The doubled && is required, not decorative. The global ring in
     globalStyles.ts is declared as a[href]:focus-visible, which is (0,2,1); a
     plain &:focus-visible is (0,2,0) and loses, so the themed ring was being
     silently replaced by the fixed grey rgba(127,127,127,0.35). That is the
     visible theme bug: grey on a dark panel, worst in light mode. The doubled
     && reaches (0,3,0) and wins. */
  &&:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => contrast(theme).panel},
      0 0 0 4px ${({ theme }) => contrast(theme).ink};
  }

  /* globalStyles.ts also underlines every a[href]:hover, at (0,2,1). These are
     navigation but they are buttons, and a filled primary action that suddenly
     underlines on hover reads as a broken link. Cancelled at (0,3,0). */
  &&:hover {
    text-decoration: none;
  }

  /* Colour transitions are kept under reduced motion; the transform guard lives
     in actionStyles below, because it has to come after the hover rule to win. */
  @media (prefers-reduced-motion: reduce) {
    transition: color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease;
  }
`;

/**
 * Primary: light fill on the dark panel. The strongest contrast pair on the
 * page, which is the point of a primary action.
 * Secondary: outline on the panel. Reachable, but visibly second.
 */
const variantStyles = {
  primary: css`
    background: ${({ theme }) => contrast(theme).ink};
    color: ${({ theme }) => contrast(theme).panel};
    border-color: ${({ theme }) => contrast(theme).ink};

    &:hover {
      background: #dcdcdc;
      border-color: #dcdcdc;
      color: ${({ theme }) => contrast(theme).panel};
    }
  `,
  secondary: css`
    background: transparent;
    color: ${({ theme }) => contrast(theme).ink};
    border-color: rgba(255, 255, 255, 0.24);

    &:hover {
      background: rgba(255, 255, 255, 0.07);
      border-color: rgba(255, 255, 255, 0.45);
      color: ${({ theme }) => contrast(theme).ink};
    }
  `,
} as const;

type ActionVariant = keyof typeof variantStyles;

const actionStyles = css<{ $variant: ActionVariant }>`
  ${actionBase};
  ${({ $variant }) => variantStyles[$variant]};
  border-style: solid;
  border-width: 1px;

  &:hover {
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  /* This guard has to sit AFTER the hover and active rules above. A media query
     adds no specificity, so :hover and :hover both weigh (0,2,0) and the later
     declaration wins outright. Written up in actionBase — the natural place for
     it — it was silently overridden by translateY(-2px) and reduced-motion users
     still got the lift. */  @media (prefers-reduced-motion: reduce) {
    &:hover,
    &:active {
      transform: none;
    }
  }
`;

/** Internal destination, rendered as a router link. */
export const ActionLink = styled(Link)<{ $variant: ActionVariant }>`
  ${actionStyles};
`;

/** External destination, rendered as a plain anchor. */
export const ActionAnchor = styled.a<{ $variant: ActionVariant }>`
  ${actionStyles};
`;
